'use client';

import React from 'react';
import Link from 'next/link';
import { openCookiePreferences } from '@/components/CookieBanner';

export default function LandingFooter() {
  const learnLinks = [
    { label: 'About', href: '/about' },
    { label: 'How It Works', href: '/how-it-works' },
    { label: 'How AI Can Help With Emails', href: '/learn/ai-for-emails' },
    { label: 'How AI Can Help You Prepare For Meetings', href: '/learn/ai-for-meeting-preparation' },
    { label: 'Copilot vs ChatGPT', href: '/copilot-vs-chatgpt' },
    { label: 'Glossary of AI Terms', href: '/glossary' },
    { label: 'What is Microsoft Copilot?', href: '/what-is-microsoft-copilot' },
    { label: 'Which AI Tool is Right for Me?', href: '/which-ai-tool-is-right-for-me' },
    { label: 'How to Write a Good AI Prompt', href: '/how-to-write-a-good-ai-prompt' },
    { label: 'Is AI Going to Replace Me?', href: '/is-ai-going-to-replace-me-at-work' },
    { label: 'AI for Professionals Over 50', href: '/ai-for-professionals-over-50' },
    { label: 'How Do I Use AI in a Job Interview?', href: '/ai-for-job-interviews' },
    { label: 'Can AI Help Me Write a Business Case?', href: '/ai-for-business-cases' },
    { label: 'Is ChatGPT Safe to Use at Work?', href: '/is-chatgpt-safe-at-work' },
    { label: 'What Can AI Do That Google Can\'t?', href: '/ai-vs-google' },
    { label: 'How Do I Use AI Without My Company Finding Out?', href: '/using-ai-at-work-privately' },
    { label: 'AI Guidance for Over 50s — Start Here', href: '/ai-training-over-50' },
    { label: 'How to Start Using AI Today', href: '/how-to-start-using-ai-over-50' },
    { label: 'Common AI Myths — Debunked', href: '/ai-myths-over-50' },
    { label: 'Why AI Feels Overwhelming', href: '/why-ai-feels-overwhelming' },
    { label: 'I Feel Left Behind By AI', href: '/i-feel-left-behind-by-ai' },
    { label: 'Am I Too Old to Learn AI?', href: '/am-i-too-old-to-learn-ai' },
    { label: 'How Much AI Do I Need to Learn?', href: '/how-much-ai-do-i-need-to-learn' },
    { label: 'The Anna Method', href: '/anna-method' },
  ];

  const chatgptLinks = [
    { label: 'How to Use ChatGPT', href: '/how-to-use-chatgpt' },
    { label: 'What ChatGPT Can Do For You', href: '/what-chatgpt-can-do-for-you' },
    { label: 'Write Better Emails with ChatGPT', href: '/how-to-write-emails-with-chatgpt' },
    { label: 'How to Ask ChatGPT Better Questions', href: '/how-to-ask-chatgpt-better-questions' },
    { label: 'ChatGPT Beginners Guide', href: '/chatgpt-beginners-guide' },
    { label: 'Mistakes Beginners Make with ChatGPT', href: '/chatgpt-mistakes-beginners-make' },
    { label: 'ChatGPT vs Google', href: '/chatgpt-vs-google' },
    { label: 'What Is Perplexity?', href: '/what-is-perplexity' },
  ];

  const aiDailyLifeLinks = [
    { label: 'AI in Everyday Life', href: '/ai-in-everyday-life' },
    { label: 'AI for Writing and Communication', href: '/ai-for-writing-and-communication' },
    { label: 'AI for Learning New Skills', href: '/ai-for-learning-new-skills' },
    { label: 'AI for Travel Planning', href: '/ai-for-travel-planning' },
    { label: 'AI for Health Information', href: '/ai-for-health-information' },
    { label: 'AI to Save Time', href: '/ai-to-save-time' },
    { label: 'Real Examples of AI Over 50', href: '/ai-real-examples-over-50' },
  ];

  const safetyLinks = [
    { label: 'Is ChatGPT Safe to Use?', href: '/is-chatgpt-safe' },
    { label: 'How to Avoid AI Scams', href: '/how-to-avoid-ai-scams' },
    { label: 'ChatGPT vs Other AI Tools', href: '/chatgpt-vs-other-ai-tools' },
    { label: 'Can AI Replace Google?', href: '/can-ai-replace-google' },
    { label: 'Best Ways to Learn AI Over 50', href: '/best-ways-to-learn-ai-over-50' },
    { label: 'How to Stay Safe with AI', href: '/how-to-stay-safe-with-ai' },
    { label: 'Complete AI Learning Roadmap', href: '/complete-ai-learning-roadmap' },
    { label: 'Top 10 Questions About AI Over 50', href: '/top-10-questions-about-ai-over-50' },
    { label: 'Start Here — AI Over 50', href: '/start-here-ai-over-50' },
    { label: 'What Should I Never Put Into AI?', href: '/what-should-i-never-put-into-ai' },
    { label: 'How to Practise AI Without Feeling Stupid', href: '/how-to-practise-ai-without-feeling-stupid' },
    { label: 'Can I Break AI?', href: '/can-i-break-ai' },
  ];

  const aiBasicsLinks = [
    { label: 'What is Artificial Intelligence?', href: '/what-is-artificial-intelligence' },
    { label: 'What is ChatGPT?', href: '/what-is-chatgpt' },
    { label: 'What is Generative AI?', href: '/what-is-generative-ai' },
    { label: 'Do You Need Technical Skills for AI?', href: '/do-you-need-technical-skills-to-use-ai' },
    { label: 'Is AI Hard to Learn?', href: '/is-ai-hard-to-learn' },
  ];

  return (
    <footer
      className="border-t"
      style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border)' }}
      role="contentinfo"
    >
      {/* Learn column */}
      <div className="max-w-screen-xl mx-auto px-6 lg:px-12 xl:px-20 py-10 border-b" style={{ borderColor: 'var(--border)' }}>
        <div>
          <p className="font-body text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: 'var(--navy)', letterSpacing: '0.12em' }}>
            LEARN
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2">
            {learnLinks?.map((link) => (
              <li key={link?.href}>
                <Link
                  href={link?.href}
                  className="font-body text-xs transition-colors duration-150 hover:opacity-80"
                  style={{ color: 'var(--text-light)' }}
                >
                  {link?.label}
                </Link>
              </li>
            ))}
          </ul>
          <p className="font-body text-xs font-semibold uppercase tracking-widest mt-6 mb-3" style={{ color: 'var(--navy)', letterSpacing: '0.10em', opacity: 0.7 }}>
            Using ChatGPT
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2">
            {chatgptLinks?.map((link) => (
              <li key={link?.href}>
                <Link
                  href={link?.href}
                  className="font-body text-xs transition-colors duration-150 hover:opacity-80"
                  style={{ color: 'var(--text-light)' }}
                >
                  {link?.label}
                </Link>
              </li>
            ))}
          </ul>
          <p className="font-body text-xs font-semibold uppercase tracking-widest mt-6 mb-3" style={{ color: 'var(--navy)', letterSpacing: '0.10em', opacity: 0.7 }}>
            AI in Daily Life
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2">
            {aiDailyLifeLinks?.map((link) => (
              <li key={link?.href}>
                <Link
                  href={link?.href}
                  className="font-body text-xs transition-colors duration-150 hover:opacity-80"
                  style={{ color: 'var(--text-light)' }}
                >
                  {link?.label}
                </Link>
              </li>
            ))}
          </ul>
          <p className="font-body text-xs font-semibold uppercase tracking-widest mt-6 mb-3" style={{ color: 'var(--navy)', letterSpacing: '0.10em', opacity: 0.7 }}>
            Safety and Comparisons
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2">
            {safetyLinks?.map((link) => (
              <li key={link?.href}>
                <Link
                  href={link?.href}
                  className="font-body text-xs transition-colors duration-150 hover:opacity-80"
                  style={{ color: 'var(--text-light)' }}
                >
                  {link?.label}
                </Link>
              </li>
            ))}
          </ul>
          <p className="font-body text-xs font-semibold uppercase tracking-widest mt-6 mb-3" style={{ color: 'var(--navy)', letterSpacing: '0.10em', opacity: 0.7 }}>
            AI Basics
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2">
            {aiBasicsLinks?.map((link) => (
              <li key={link?.href}>
                <Link
                  href={link?.href}
                  className="font-body text-xs transition-colors duration-150 hover:opacity-80"
                  style={{ color: 'var(--text-light)' }}
                >
                  {link?.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
      {/* Bottom bar */}
      <div className="py-8">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12 xl:px-20 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" height="24" width="24" aria-label="ThatClicked brand mark" role="img">
              <path d="M100 48 C78 48 60 66 60 88 C60 103 68 116 80 123 L80 132 C80 134 82 136 84 136 L116 136 C118 136 120 134 120 132 L120 123 C132 116 140 103 140 88 C140 66 122 48 100 48Z" fill="#D4891A" opacity="0.15"/>
              <path d="M100 48 C78 48 60 66 60 88 C60 103 68 116 80 123 L80 132 C80 134 82 136 84 136 L116 136 C118 136 120 134 120 132 L120 123 C132 116 140 103 140 88 C140 66 122 48 100 48Z" fill="none" stroke="#D4891A" strokeWidth="3.5" strokeLinejoin="round"/>
              <path d="M100 54 C81 54 66 69 66 88 C66 101 73 112 84 119 L84 130 L116 130 L116 119 C127 112 134 101 134 88 C134 69 119 54 100 54Z" fill="#D4891A" opacity="0.08"/>
              <line x1="100" y1="72" x2="100" y2="118" stroke="#D4891A" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
              <line x1="88" y1="82" x2="100" y2="72" stroke="#D4891A" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
              <line x1="112" y1="82" x2="100" y2="72" stroke="#D4891A" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
              <line x1="88" y1="98" x2="88" y2="82" stroke="#D4891A" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
              <line x1="112" y1="98" x2="112" y2="82" stroke="#D4891A" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
              <line x1="88" y1="98" x2="100" y2="118" stroke="#D4891A" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
              <line x1="112" y1="98" x2="100" y2="118" stroke="#D4891A" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
              <rect x="82" y="138" width="36" height="7" rx="2" fill="#D4891A" opacity="0.9"/>
              <rect x="82" y="148" width="36" height="7" rx="2" fill="#D4891A" opacity="0.7"/>
              <rect x="86" y="158" width="28" height="5" rx="2.5" fill="#D4891A" opacity="0.5"/>
              <line x1="58" y1="58" x2="50" y2="50" stroke="#D4891A" strokeWidth="2.5" strokeLinecap="round" opacity="0.7"/>
              <line x1="50" y1="58" x2="58" y2="50" stroke="#D4891A" strokeWidth="2.5" strokeLinecap="round" opacity="0.7"/>
              <line x1="142" y1="58" x2="150" y2="50" stroke="#D4891A" strokeWidth="2.5" strokeLinecap="round" opacity="0.7"/>
              <line x1="150" y1="58" x2="142" y2="50" stroke="#D4891A" strokeWidth="2.5" strokeLinecap="round" opacity="0.7"/>
            </svg>
            <span className="font-body text-sm font-medium" style={{ color: 'var(--navy)' }}>
              That Clicked
            </span>
          </div>

          <div className="flex flex-col items-center gap-1">
            <p className="font-body text-xs text-center" style={{ color: 'var(--text-light)' }}>
              AI confidence for professionals over 50.
            </p>
            <p className="font-body text-xs" style={{ color: 'var(--text-light)' }}>
              © 2026 That Clicked. All rights reserved.
            </p>
          </div>

          <div className="flex items-center gap-6">
            <Link
              href="/about"
              className="font-body text-xs transition-colors duration-150 hover:text-navy-500"
              style={{ color: 'var(--text-light)' }}
            >
              About
            </Link>
            <Link
              href="/privacy"
              className="font-body text-xs transition-colors duration-150 hover:text-navy-500"
              style={{ color: 'var(--text-light)' }}
            >
              Privacy Policy
            </Link>
            <Link
              href="/terms"
              className="font-body text-xs transition-colors duration-150 hover:text-navy-500"
              style={{ color: 'var(--text-light)' }}
            >
              Terms of Service
            </Link>
            <Link
              href="/california-privacy"
              className="font-body text-xs transition-colors duration-150 hover:text-navy-500"
              style={{ color: 'var(--text-light)' }}
            >
              California Privacy
            </Link>
            <button
              onClick={openCookiePreferences}
              className="font-body text-xs transition-colors duration-150 hover:text-navy-500 bg-transparent border-0 p-0 cursor-pointer"
              style={{ color: 'var(--text-light)' }}
            >
              Cookie Preferences
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}